const FONT_SIZE_MAP: Record<string, string> = {
  small: '14px',
  normal: '16px',
  large: '20px',
  xl: '24px',
}

export function resolveTextWidgetFontSize(fontSize?: string): string {
  if (!fontSize) return '16px'
  if (FONT_SIZE_MAP[fontSize]) return FONT_SIZE_MAP[fontSize]
  return fontSize
}

import { processTextContent } from '@/utils/textProcessing'

type TypographyStripOptions = {
  color?: boolean
  fontFamily?: boolean
  fontSize?: boolean
}

function cleanInlineStyle(
  styleValue: string,
  strip: TypographyStripOptions,
): string {
  const blocked: RegExp[] = []
  if (strip.color) blocked.push(/^color\s*:/i)
  if (strip.fontFamily) blocked.push(/^font-family\s*:/i)
  if (strip.fontSize) blocked.push(/^font-size\s*:/i)

  return styleValue
    .split(';')
    .map((rule) => rule.trim())
    .filter((rule) => rule && !blocked.some((pattern) => pattern.test(rule)))
    .join('; ')
}

function stripTypographyFromHtml(html: string, strip: TypographyStripOptions): string {
  if (!html) return ''

  let result = html
    .replace(/\sstyle="([^"]*)"/gi, (_match, styleValue: string) => {
      const cleaned = cleanInlineStyle(styleValue, strip)
      return cleaned ? ` style="${cleaned}"` : ''
    })
    .replace(/\sstyle='([^']*)'/gi, (_match, styleValue: string) => {
      const cleaned = cleanInlineStyle(styleValue, strip)
      return cleaned ? ` style='${cleaned}'` : ''
    })

  if (strip.color) {
    result = result.replace(/\scolor="[^"]*"/gi, '').replace(/\scolor='[^']*'/gi, '')
  }

  if (strip.fontFamily) {
    result = result
      .replace(/\sclass="([^"]*)"/gi, (_match, classValue: string) => {
        const cleaned = classValue
          .split(/\s+/)
          .filter((cls) => cls && !/^ql-font-/i.test(cls))
          .join(' ')
        return cleaned ? ` class="${cleaned}"` : ''
      })
      .replace(/\sface="[^"]*"/gi, '')
      .replace(/\sface='[^']*'/gi, '')
  }

  return result
}

/** Remove inline typography so widget-level style settings apply on the frontend. */
export function stripInlineTypography(
  html: string,
  options: TypographyStripOptions = { color: true, fontFamily: true, fontSize: true },
): string {
  return stripTypographyFromHtml(html, options)
}

/** Remove inline color styles so widget-level textColor can apply on the frontend. */
export function stripInlineTextColors(html: string): string {
  return stripInlineTypography(html, { color: true, fontFamily: false, fontSize: false })
}

export function processRichTextContent(content: string, textColor?: string): string {
  let html = processTextContent(content || '')
  if (textColor) {
    html = stripInlineTextColors(html)
  }
  return html
}
